import { useState, useEffect } from 'react';
import {
  getCartCookieOnClient,
  getPPIDAttributes
} from '@/lib/shopify/cart/utils';
import { createCartAndSetCookie } from '@/components/commerce/cart/actions';
import { getCart } from '@/lib/shopify';
import { logCartEvent } from '@/lib/cart/logCartEvent';
import { recoverFromStaleServerAction } from '@/lib/errors/staleServerAction';

/**
 * This hook ensures that the cart is loaded and available for use, and provides
 * a function to update it after you make changes. It reads the cart by checking
 * for a cart cookie, and if it doesn't find one, it creates a new cart and
 * sets the cookie.
 */
export default function useShopifyCart() {
  const [cart, setCart] = useState(null);
  useEffect(() => {
    const getInitialCart = async () => {
      // Declared outside the try so the fallback below can reuse it; otherwise
      // the retry drops PPID attribution.
      let trackingAttributes = [];
      try {
        trackingAttributes = getPPIDAttributes();
        const checkoutId = getCartCookieOnClient();

        if (checkoutId) {
          const initialCart = await getCart(checkoutId, trackingAttributes);
          if (!initialCart) {
            const newCart = await createCartAndSetCookie(trackingAttributes);
            setCart(newCart);
          } else {
            setCart(initialCart);
          }
        } else {
          const newCart = await createCartAndSetCookie(trackingAttributes);
          setCart(newCart);
        }
      } catch (error) {
        // Logged before recovering so RUM keeps seeing this error class. The
        // reload replaces the outdated bundle, so skip the fallback below,
        // which would call the same missing action and fail the same way.
        console.error('Error initializing cart. Creating new one:', error);
        logCartEvent(
          'CART_INITIALIZATION_ERROR',
          {
            error: error.message,
            stack: error.stack,
            checkoutId: getCartCookieOnClient(),
            fallback_action: 'creating_new_cart',
          },
          'warn',
          { keepalive: true },
        );
        if (recoverFromStaleServerAction(error, { stage: 'cart_init' })) return;

        try {
          const newCart = await createCartAndSetCookie(trackingAttributes);
          setCart(newCart);
        } catch (createError) {
          console.error('Failed to create fallback cart:', createError);
          logCartEvent(
            'CREATE_FALLBACK_CART_FAILED',
            { error: createError.message, stack: createError.stack },
            'error',
            { keepalive: true },
          );
          recoverFromStaleServerAction(createError, { stage: 'cart_fallback' });
        }
      }
    };
    getInitialCart();
  }, []);

  return [cart, setCart];
}
